import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { HeartIcon, ShieldIcon, MessageCircleIcon, CompassIcon, CheckCircle2Icon } from 'lucide-react';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { SubpageHero } from '../../components/sections/subpages/SubpageHero';
import { PatternDecor } from '../../components/ui/PatternDecor';
import { Link } from 'react-router-dom';
const skills = [{
  icon: HeartIcon,
  title: 'Self-awareness',
  body: 'Children learn to understand their own feelings, values, and reactions before responding to others.',
  bg: 'bg-sage-100'
}, {
  icon: MessageCircleIcon,
  title: 'Respectful communication',
  body: 'Tools for engaging with peers and adults from different backgrounds with calm and confidence.',
  bg: 'bg-[#EDF2F7]'
}, {
  icon: ShieldIcon,
  title: 'Healthy boundaries',
  body: 'Knowing when to say no, how to disagree respectfully, and how to ask for help when needed.',
  bg: 'bg-[#F7EDE9]'
}, {
  icon: CompassIcon,
  title: 'Ethical decision-making',
  body: 'Practical frameworks for everyday choices — rooted in values, applied in real life.',
  bg: 'bg-sand-100'
}];
export function ValuesIdentity() {
  return <>
      <SubpageHero eyebrow="Strong identity. Calm confidence. Respectful integration." title="Values & Identity" paragraphs={['We believe Islamic identity should be a source of strength — not fear of who they are, isolation, or pressure.', 'Our work helps children grow into confident Muslims who respect others, understand themselves, and navigate cultural differences with maturity and peace.']} primaryCta={{
      label: 'Explore Programs',
      to: '/en/children/kids-programs'
    }} secondaryCta={{
      label: 'Scholarships & Support',
      to: '/en/children/scholarships'
    }} image="https://cdn.magicpatterns.com/uploads/f2BL8YAy91ePHt862eZ6yr/Values.jpg" imageAlt="Muslim father and son reading a storybook together at home" compact masked data-id="element-3455" data-mp="2d6koepvecmfo" />

      {/* Daily life skills */}
      <section className="py-16 md:py-24 bg-sage-50/60" data-id="element-3456" data-mp="13gspuz74ils6">
        <Container data-id="element-3457" data-mp="yi7ouxk1ry2o">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-3458" data-mp="yjsj0m2g9f00">
            Identity is built through daily life skills
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-3459" data-mp="6rl5hui8qipu">
            Children don't develop confidence through information alone. They
            develop it through guidance, language, boundaries, and emotional
            resilience — practiced consistently in everyday situations.
          </p>
          <div className="mt-10 grid md:grid-cols-2 gap-5" data-id="element-3460" data-mp="1w5zpy2m87jfj">
            {skills.map((s, i) => <motion.div key={s.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06
          }} className={`rounded-2xl ${s.bg} border border-ink-900/5 p-6 flex gap-4`} data-id="element-3461" data-mp="1r7ex4o9b0214" data-mpi={i}>
                <span className="w-11 h-11 inline-flex items-center justify-center rounded-full bg-white border border-forest-700/10 text-forest-700 flex-shrink-0" data-id="element-3462" data-mp="oq8r47w2c4o">
                  <s.icon size={18} strokeWidth={1.6} data-id="element-3463" data-mp="3j0v5ul8uezlz" />
                </span>
                <div data-id="element-3464" data-mp="3o0hiotkredu9">
                  <h3 className="font-display text-lg text-ink-900" data-id="element-3465" data-mp="3nzxy153q5bcj">
                    {s.title}
                  </h3>
                  <p className="mt-1 text-ink-700 text-sm leading-relaxed" data-id="element-3466" data-mp="kcz1pgh9am58">
                    {s.body}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </Container>
      </section>

      {/* Principles */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-white/60" data-id="element-3467" data-mp="2mw4hdbzeziw6">
        <PatternDecor side="right" y="top" size="lg" data-id="element-3468" data-mp="2iwdvnmoqvsyi" />
        <Container data-id="element-3469" data-mp="2dx0ya808z3ho">
          <div className="grid lg:grid-cols-12 gap-12 items-center" data-id="element-3470" data-mp="2dxizlj1ro7wg">
            <div className="lg:col-span-6" data-id="element-3471" data-mp="3ams433dzod2s">
              <h2 className="font-display text-3xl md:text-4xl text-ink-900 leading-tight" data-id="element-3472" data-mp="3alqi43vfq66a">
                Our approach to identity
              </h2>
              <ul className="mt-6 space-y-4 text-ink-700" data-id="element-3473" data-mp="31uq2v2w3isrg">
                {['Identity is a strength, never a barrier', 'Confidence is taught — through example, language, and practice', 'Children should respect themselves and others equally', 'Difference is acknowledged calmly, not avoided or amplified', 'Faith is lived sincerely — through worship and character'].map(p => <li key={p} className="flex items-start gap-3" data-id="element-3474" data-mp="5p4h3va4949k">
                    <CheckCircle2Icon size={18} className="text-forest-700 mt-1 flex-shrink-0" strokeWidth={1.6} data-id="element-3475" data-mp="5pnx36g7hiky" />
                    <span data-id="element-3476" data-mp="er5dyr1ftoap">{p}</span>
                  </li>)}
              </ul>
            </div>
            <div className="lg:col-span-6" data-id="element-3477" data-mp="jmt3x8nqulh9">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-card" data-id="element-3478" data-mp="ongujt8290ay">
                <img src="https://cdn.magicpatterns.com/uploads/82e2kLQDrubbGnRrEfyVgg/identuty.jpg" alt="Young Muslim girl writing at a desk by a sunlit window" className="w-full h-full object-cover" data-id="element-3479" data-mp="teyviha330vx" />
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Ready to begin */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-white/60" data-id="element-3480" data-mp="33hmfvihfolxo">
        <PatternDecor side="left" y="middle" size="md" data-id="element-3481" data-mp="33f9yb6fl8dl5" />
        <Container data-id="element-3482" data-mp="2zhc8q2hwnph8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8" data-id="element-3483" data-mp="2zhvr1kbgwfvs">
            <div className="max-w-xl" data-id="element-3484" data-mp="3d90nkdjjgs1w">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-3485" data-mp="38gzcuw20r9dz">
                Ready to begin?
              </h2>
              <p className="mt-4 text-ink-700" data-id="element-3486" data-mp="3n4aloq6tgfae">
                Choose a program that matches your child's age and needs — and
                start building confidence, values, and belonging in a safe
                learning space.
              </p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-3487" data-mp="3r6p7nj5s7v5w">
              <Link to="/en/children/kids-programs" data-id="element-3488" data-mp="3r806r5oa8eqo">
                <Button variant="primary" data-id="element-3489" data-mp="21rhbrwrw7grq">View Kids Programs</Button>
              </Link>
              <Link to="/en/children/scholarships" data-id="element-3490" data-mp="1wtpjh1nc5sn0">
                <Button variant="secondary" data-id="element-3491" data-mp="1wv0mv0cvcydl">Apply for a Scholarship</Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>;
}